<template>
  <UiStepProgressIndicator
    v-if="isDisplayed"
    :steps="steps"
    :current-index="index"
  />
</template>

<script setup lang="ts">
import { computed, nextTick, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { store } from '@/app/store';
import { useUiStore } from '@/app/stores/ui';
import { contrastRatio } from '@/shared/utils/color-contrast';
import type { UiStep } from '@/ui-kit';
// biome-ignore lint/correctness/noUnusedImports: used via tag <UiStepProgressIndicator> in <template>
import { UiStepProgressIndicator } from '@/ui-kit';

interface Props {
  index?: number;
}

const props = withDefaults(defineProps<Props>(), {
  index: -1,
});

const { t } = useI18n();
const ui = useUiStore();

const isDisplayed = computed(() => props.index > -1 && !ui.isStepContentHidden);

const steps = computed<UiStep[]>(() => {
  const tr = (key: string): string => t(`StepProgressIndicator.${key}`);
  const isPickup = store.kiosk?.gophoto_printing;
  return [
    { long: tr('photoLong'), short: tr('photoShort') },
    { long: tr('facesLong'), short: tr('facesShort') },
    { long: tr('portraitsLong'), short: tr('portraitsShort') },
    {
      long: isPickup ? tr('sendToPickupLong') : tr('printLong'),
      short: isPickup ? tr('sendToPickupShort') : tr('printShort'),
    },
  ];
});

function setStepNumberColors(): void {
  const rootStyles = getComputedStyle(document.documentElement);
  const bgColor = rootStyles
    .getPropertyValue('--Kiosk--background_color')
    .trim();
  const buttonBg = rootStyles
    .getPropertyValue('--Kiosk--button_background_color')
    .trim();
  const buttonFont = rootStyles
    .getPropertyValue('--Kiosk--button_font_color')
    .trim();

  const contrastWithBg = contrastRatio(bgColor, buttonBg);
  const contrastWithFont = contrastRatio(bgColor, buttonFont);

  const bestBgColor = contrastWithBg > contrastWithFont ? buttonBg : buttonFont;
  const bestTextColor =
    contrastWithBg > contrastWithFont ? buttonFont : buttonBg;

  document.documentElement.style.setProperty(
    '--ui-step-progress-number-bg',
    bestBgColor
  );
  document.documentElement.style.setProperty(
    '--ui-step-progress-number-color',
    bestTextColor
  );
}

onMounted(() => {
  nextTick(() => setStepNumberColors());
});
</script>
